import { z } from 'zod';

/**
 * Stripe Webhook Event Types
 */
export enum StripeWebhookEventType {
  BUSINESS_SUBSCRIPTION_CREATED = 'business.subscription.created',
  BUSINESS_SUBSCRIPTION_UPDATED = 'business.subscription.updated',
  BUSINESS_SUBSCRIPTION_DELETED = 'business.subscription.deleted',
  INVOICE_PAYMENT_SUCCEEDED = 'invoice.payment_succeeded',
  INVOICE_PAYMENT_FAILED = 'invoice.payment_failed',
  BUSINESS_CREATED = 'business.created',
  BUSINESS_UPDATED = 'business.updated',
  BUSINESS_DELETED = 'business.deleted',
}

/**
 * Stripe Webhook Event Schema
 */
export const StripeWebhookEventSchema = z.object({
  id: z.string(),
  object: z.literal('event'),
  type: z.nativeEnum(StripeWebhookEventType),
  data: z.object({
    object: z.record(z.string(), z.any()),
  }),
  created: z.number(),
  livemode: z.boolean(),
  pending_webhooks: z.number().optional(),
  request: z
    .object({
      id: z.string().nullable(),
      idempotency_key: z.string().nullable(),
    })
    .nullable()
    .optional(),
});

export type StripeWebhookEvent = z.infer<typeof StripeWebhookEventSchema>;

/**
 * Stripe Subscription Object Schema (from webhook)
 */
export const StripeSubscriptionSchema = z.object({
  id: z.string(),
  object: z.literal('subscription'),
  business: z.string(),
  status: z.enum(['incomplete', 'incomplete_expired', 'trialing', 'active', 'past_due', 'canceled', 'unpaid']),
  items: z.object({
    data: z.array(
      z.object({
        id: z.string(),
        price: z.object({
          id: z.string(),
          product: z.string(),
        }),
      }),
    ),
  }),
  current_period_start: z.number(),
  current_period_end: z.number(),
  cancel_at: z.number().nullable().optional(),
  canceled_at: z.number().nullable().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

export type StripeSubscription = z.infer<typeof StripeSubscriptionSchema>;

/**
 * Stripe Invoice Object Schema (from webhook)
 */
export const StripeInvoiceSchema = z.object({
  id: z.string(),
  object: z.literal('invoice'),
  business: z.string(),
  subscription: z.string().nullable(),
  amount_paid: z.number(),
  amount_due: z.number(),
  currency: z.string(),
  status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']),
  payment_intent: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

export type StripeInvoice = z.infer<typeof StripeInvoiceSchema>;
